import { zodResolver } from '@hookform/resolvers/zod'
import { type RefObject, useEffect } from 'react'
import {
	FormProvider,
	type SubmitHandler,
	useFieldArray,
	useForm
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { useOutletContext } from 'react-router'
import { CharCounter } from '@/components/commons/char-counter'
import { InputText } from '@/components/commons/input-text'
import { TextArea } from '@/components/commons/textarea'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	ArrowRightIcon,
	PlusIcon,
	Stars01Icon,
	Trash01Icon,
	User01Icon
} from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import type { OpsVettingURLParams } from '@/features/ops-vetting/types'
import { ServiceVettingMerchFormSkeleton } from '@/features/service-vetting/components/service-vetting-merch-form-skeleton'
import { useTranslation } from '@/translations/i18n'
import { OpsVettingMerchFormHeader } from '../../ops-vetting/components/ops-vetting-merch-form-header'
import {
	EXPERIENCE_OUTPUT_CHARACTER_LIMITS,
	ExperienceVettingMerchOutputSchema
} from '../form-schemas'
import { useExperienceVettingSaveMerchCopy } from '../queries/use-experience-vetting-save-merch-copy'

interface ExpVettingMerchOutputFormProps {
	isTraining?: boolean
	isOriginalContent?: boolean
	isLoading?: boolean
	isReadOnly?: boolean
	isHostEdits?: boolean
	defaultValues?: ExperienceVettingMerchOutputSchema
	finalSubmitRef: RefObject<HTMLButtonElement | null>
	handleFinalSubmissionCb?: SubmitHandler<ExperienceVettingMerchOutputSchema>
}

export const ExpVettingMerchOutputForm = ({
	isTraining = false,
	isOriginalContent = false,
	isLoading = false,
	isReadOnly = false,
	isHostEdits = false,
	defaultValues,
	finalSubmitRef,
	handleFinalSubmissionCb
}: ExpVettingMerchOutputFormProps) => {
	const { t } = useTranslation()
	const params = useOutletContext<{ urlParams: OpsVettingURLParams }>()
	const experienceId = params?.urlParams.id
	const methods = useForm({
		resolver: isOriginalContent
			? undefined
			: zodResolver(ExperienceVettingMerchOutputSchema),
		defaultValues
	})

	const {
		register,
		control,
		reset,
		getValues,
		formState: { errors }
	} = methods
	const { fields, remove, append } = useFieldArray({
		control,
		name: 'activities'
	})

	useEffect(() => {
		if (defaultValues) {
			reset(defaultValues)
		}
	}, [defaultValues, reset])

	const { mutate: saveMerchCopy, isPending: isSavingMerchCopy } =
		useExperienceVettingSaveMerchCopy(
			{ experienceId },
			{
				onSuccess: () =>
					toast.success(
						t(
							'vetting:merch.experience.generated_content_saved_tooltip'
						)
					)
			}
		)
	//final submission
	const handleSaveServiceMerchVetting = () => {
		const payload = {
			...getValues(),
			isHostApprovalNeeded: false,
			isWaitlisted: false
		}
		saveMerchCopy(payload)
	}

	return (
		<FormProvider {...methods}>
			<form
				className={`h-full flex-1 p-6 ${isOriginalContent ? 'bg-neutral-white' : 'bg-neutral-50'}`}
				onSubmit={methods.handleSubmit(
					handleFinalSubmissionCb || (() => Function.prototype)
				)}
			>
				{!isOriginalContent && (
					<OpsVettingMerchFormHeader
						description={t(
							'vetting:merch.experience.output_shell_form_description'
						)}
						endSlot={
							isTraining ? undefined : (
								<ButtonLegacy
									disabled={
										isSavingMerchCopy ||
										isReadOnly ||
										isLoading
									}
									onClick={handleSaveServiceMerchVetting}
									type='button'
									variant='secondary'
								>
									{t('common:save')}
									{isSavingMerchCopy && <Spinner size={16} />}
								</ButtonLegacy>
							)
						}
						icon={isHostEdits ? <User01Icon /> : <Stars01Icon />}
						title={
							isHostEdits
								? t(
										'vetting:merch.experience.input_vetting_form_title'
									)
								: t(
										'vetting:merch.experience.output_shell_form_title'
									)
						}
						variant={'blue'}
					/>
				)}
				{isOriginalContent && (
					<OpsVettingMerchFormHeader
						description={
							isHostEdits
								? t(
										'vetting:merch.experience.output_vestting_edits_description'
									)
								: t(
										'vetting:merch.experience.input_vetting_form_description'
									)
						}
						icon={isHostEdits ? <ArrowRightIcon /> : <User01Icon />}
						title={
							isHostEdits
								? t(
										'vetting:merch.experience.output_vestting_edits_title'
									)
								: t(
										'vetting:merch.experience.input_vetting_form_title'
									)
						}
						variant={'base'}
					/>
				)}

				{isLoading ? (
					<ServiceVettingMerchFormSkeleton />
				) : (
					<div className='flex w-full flex-col gap-2'>
						<div className='flex w-full flex-col gap-5 py-6 text-neutral-900'>
							<h4 className='font-medium text-base'>
								{t('common:experience')}
							</h4>
							<div className='flex w-full flex-col'>
								<div className='flex w-full flex-col'>
									<InputText
										disabled={isReadOnly}
										errorMessage={
											errors?.improved_experience_title
												?.message
										}
										label={t(
											'vetting:merch.experience.listing_title_label'
										)}
										type={'text'}
										{...register(
											'improved_experience_title'
										)}
									/>
									<CharCounter<ExperienceVettingMerchOutputSchema>
										isDisabled={isOriginalContent}
										maxLength={
											EXPERIENCE_OUTPUT_CHARACTER_LIMITS
												.EXPERIENCE.TITLE
										}
										name='improved_experience_title'
									/>
								</div>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.listing_description_label'
									)}
									rows={3}
									{...register(
										'improved_experience_description'
									)}
									errorMessage={
										errors?.improved_experience_description
											?.message
									}
								/>
								<CharCounter<ExperienceVettingMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS
											.EXPERIENCE.DESCRIPTION
									}
									name='improved_experience_description'
								/>
							</div>

							{fields.map((activity, index) => (
								<div
									className='flex w-full flex-col gap-3'
									key={activity.id}
								>
									<div className='flex items-center justify-between gap-4'>
										<p className='font-medium text-sm'>
											{t(
												'vetting:merch.experience.activity_input_label',
												{
													index: index + 1
												}
											)}
										</p>
										{isTraining &&
											isOriginalContent &&
											fields.length > 1 && (
												<ButtonLegacy
													onClick={() =>
														remove(index)
													}
													size='xs'
													type='button'
													variant='tertiary'
												>
													<Trash01Icon />
												</ButtonLegacy>
											)}
									</div>

									<div className='flex w-full flex-col'>
										<InputText
											disabled={isReadOnly}
											errorMessage={
												errors?.activities?.[index]
													?.improved_title?.message
											}
											label={t(
												'vetting:merch.experience.activity_title_label'
											)}
											type={'text'}
											{...register(
												`activities.${index}.improved_title`
											)}
										/>
										<CharCounter<ExperienceVettingMerchOutputSchema>
											isDisabled={isOriginalContent}
											maxLength={
												EXPERIENCE_OUTPUT_CHARACTER_LIMITS
													.ACTIVITY.TITLE
											}
											name={`activities.${index}.improved_title`}
										/>
									</div>

									<div className='flex w-full flex-col'>
										<TextArea
											disabled={isReadOnly}
											errorMessage={
												errors?.activities?.[index]
													?.improved_description
													?.message
											}
											label={t(
												'vetting:merch.experience.activity_description_label'
											)}
											rows={3}
											{...register(
												`activities.${index}.improved_description`
											)}
										/>
										<CharCounter<ExperienceVettingMerchOutputSchema>
											isDisabled={isOriginalContent}
											maxLength={
												EXPERIENCE_OUTPUT_CHARACTER_LIMITS
													.ACTIVITY.DESCRIPTION
											}
											name={`activities.${index}.improved_description`}
										/>
									</div>
								</div>
							))}
							{isTraining &&
								isOriginalContent &&
								fields.length < 10 && (
									<ButtonLegacy
										className='self-start'
										onClick={() =>
											append({
												improved_title: '',
												improved_description: ''
											})
										}
										size='sm'
										type='button'
										variant='tertiary'
									>
										<PlusIcon />
										{t(
											'vetting:merch.experience.add_activity_label'
										)}
									</ButtonLegacy>
								)}
						</div>

						<Separator orientation='horizontal' />

						<div className='flex w-full flex-col gap-4 py-6 text-neutral-900'>
							<h4 className='font-medium text-base'>
								{t('common:host')}
							</h4>
							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.expertise__tile_label'
									)}
									type={'text'}
									{...register(
										'improved_host_experience_title'
									)}
									errorMessage={
										errors?.improved_host_experience_title
											?.message
									}
								/>
								<CharCounter<ExperienceVettingMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS.HOST
											.TITLE
									}
									name='improved_host_experience_title'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.expertise_label'
									)}
									rows={3}
									{...register(
										'improved_host_expertise_description'
									)}
									errorMessage={
										errors
											?.improved_host_expertise_description
											?.message
									}
								/>
								<CharCounter<ExperienceVettingMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS.HOST
											.BIO
									}
									name='improved_host_expertise_description'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.recognition_title_label'
									)}
									type={'text'}
									{...register(
										'improved_host_recognition_title'
									)}
									errorMessage={
										errors?.improved_host_recognition_title
											?.message
									}
								/>
								<CharCounter<ExperienceVettingMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS.HOST
											.RECOGNITION_TITLE
									}
									name='improved_host_recognition_title'
								/>
							</div>
						</div>
					</div>
				)}
				<button
					ref={finalSubmitRef}
					style={{ display: 'none' }}
					type='submit'
				/>
			</form>
		</FormProvider>
	)
}
